import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { IEventDetailsDTO } from '../../../../../../api/events.dao';
import {
    EmailGroupItem,
    IScheduledEmailDAO,
    IScheduledEmailDTO,
    IScheduledEmailsListDTO,
    ScheduledEmailSentStatus,
    ScheduledEmailStatus,
    ScheduledTypes
} from '../../../../../../api/scheduled-email.dao';
import { IErrorParsing } from '../../../../../../infrastructure/error-parsing';
import { ISubWindowFacade } from '../../../../../../infrastructure/sub-windows';
import { alertServerError } from '../../../../../global/modal/alerts-host/alert-templates';
import { TObject } from '../../../../../../infrastructure/type-extensions';
import {
    FETCHING_SCHEDULED_EMAILS_API_ERROR,
} from './scheduled-emails-form.texts';
import { ScheduledMeetingsEmailsVM } from './_scheduled-meeting-emails-form.viewmodel';
import { ScheduledEventEmailsVM } from './_scheduled-events-emails-form.viewmodel';
import { transpileInlineStylesToClasses } from '../../../../../global/rich-text-editor/utils/transpiler';

const defaultScheduledTypes = [ScheduledTypes.Event, ScheduledTypes.Meeting];

class ScheduledEmailsFormManagement {
    private scheduledTypes: ScheduledTypes[];
    @mobx.observable
    public isLoading: boolean;
    @mobx.observable
    private scheduledEventEmailsVM: ScheduledEventEmailsVM;
    @mobx.observable
    private scheduledMeetingEmailsVM: ScheduledMeetingsEmailsVM;

    constructor(
        private accountId: number,
        private _event: IEventDetailsDTO,
        private emailIdToEditPopupMap: TObject<ISubWindowFacade>,
        scheduledTypes?: ScheduledTypes[],
        @IoC.Inject private readonly scheduledEmailDAO: IScheduledEmailDAO = IoC.Container.get(IScheduledEmailDAO),
        @IoC.Inject private readonly errorParsing = IoC.Container.get(IErrorParsing),
    ) {
        this.scheduledTypes = scheduledTypes || defaultScheduledTypes;
     }

    public get types() : ScheduledTypes[] {
        return this.scheduledTypes;
    }

    public getProperVM(type: ScheduledTypes): ScheduledEventEmailsVM | ScheduledMeetingsEmailsVM {
        switch (type) {
            case ScheduledTypes.Meeting:
                return this.scheduledMeetingEmailsVM;
            case ScheduledTypes.Event:
            default:
                return this.scheduledEventEmailsVM;
        }
    }

    public async fetchScheduledEmails(): Promise<void> {
        this.isLoading = true;

        try {
            const result = await this.scheduledEmailDAO.getScheduledEmails({
                accountId: this.accountId,
                eventId: this._event.eventId,
            });

            result.forEach(email => {
                // In case we have body with Rich Text editor styles, we need to transpile them to classes
                if (email?.emailTemplate?.body) {
                    email.emailTemplate.body = transpileInlineStylesToClasses(email.emailTemplate.body);
                }
            });

            this.onFetchScheduledEmailsSuccess(result);
        } catch (error) {
            this.onFetchScheduledEmailsFail(error);
        }
    }

    public getEmailGroups(): EmailGroupItem[] {
        const emailGroups: EmailGroupItem[] = [];
        this.scheduledTypes.forEach(scheduledType => {
            const viewModel = this.getProperVM(scheduledType);
            const emails = viewModel.getSortedEmails();

            const sentEmails: IScheduledEmailDTO[] = [];
            const otherEmails: IScheduledEmailDTO[] = [];

            emails.forEach(email => {
                if (email.status === ScheduledEmailStatus.SUCCEEDED) {
                    sentEmails.push(email);
                } else {
                    otherEmails.push(email);
                }
            });

            emailGroups.push(
                {
                    type: scheduledType,
                    status: ScheduledEmailSentStatus.Sent,
                    emails: sentEmails,
                    viewModel: viewModel,
                },
                {
                    type: scheduledType,
                    status: ScheduledEmailSentStatus.Other,
                    emails: otherEmails,
                    viewModel: viewModel,
                }
            );
        });
        return emailGroups;
    };

   public getSortedEmailGroups(): EmailGroupItem[] {
        const emailGroups: EmailGroupItem[] = this.getEmailGroups();
        const typeOrder: Record<ScheduledTypes, number> = {
            [ScheduledTypes.Event]: 0,
            [ScheduledTypes.Meeting]: 1,
        };
        const statusOrder: Record<ScheduledEmailSentStatus, number> = {
            Other: 0,
            Sent: 1,
        };
        return emailGroups.sort((a, b) => {
            const statusDiff = statusOrder[a.status] - statusOrder[b.status];
            if (statusDiff !== 0) {
                return statusDiff;
            }
            return typeOrder[a.type] - typeOrder[b.type];
        });
    };

    @mobx.action
    private onFetchScheduledEmailsSuccess = (data: IScheduledEmailsListDTO): void => {
        const eventEmails = data.filter(x => !x.meetingId);
        const meetingEmails = data.filter(x => x.meetingId);

        this.scheduledEventEmailsVM = new ScheduledEventEmailsVM(
            this.accountId, this._event, eventEmails, this.emailIdToEditPopupMap
        );
        this.scheduledMeetingEmailsVM = new ScheduledMeetingsEmailsVM(
            this.accountId, this._event, meetingEmails, this.emailIdToEditPopupMap
        );

        this.isLoading = false;
    };


    @mobx.action
    private onFetchScheduledEmailsFail(error): void {
        this.isLoading = false;
        const errorMessage = this.errorParsing.extractMessage(error);
        alertServerError(FETCHING_SCHEDULED_EMAILS_API_ERROR, errorMessage);
    }
}

export { ScheduledEmailsFormManagement };
